import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import * as io from "socket.io-client";

import "./App.scss";
import { SERVER_URL, GOOGLE_CLIENT_ID } from "./constants/api";
import ApiService from "./services/api-service";
import { setLanguage } from "./action/language";
import common_en from "./translations/en/common.json";
import RouterOutlet from "./pages/router-outlet";
import { UserContext } from "./context/userContext";
import { SocketContext } from "./context/socket";
import cookieStorage from "./services/cookie-storage";

// STEP 1: import AudioProvider
import { AudioProvider } from "./context/AudioProvider";
// Import Safari helpers
import { applySafariCSSFixes, isSafari } from "./utils/safariHelpers";

// Replace your detectAndSaveTimezone function with this

function detectAndSaveTimezone(token) {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (timezone) {
      // console.log(`Detected timezone: ${timezone}, sending to server...`);
      
      ApiService.SET_USER_TIMEZONE(timezone)
        .then(response => {
          if (response && response.result && response.result.success) {
            // console.log('Timezone saved successfully');
          } else {
            // console.warn('Failed to save timezone', response);
          }
        })
        .catch(error => {
          // console.error('Error saving timezone:', error);
        });
    }
  } catch (e) {
    // console.error("Could not detect timezone", e);
  }
}

function App() {
  const [userData, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { languageObject, language } = useSelector((state) => state.language);
  const dispatch = useDispatch();
  const token = cookieStorage.getCookie("Authorization");

  const [Socket, setSocket] = useState();
  const value = useMemo(() => ({ userData, setUser }), [userData, setUser]);
  const socket = useMemo(() => ({ Socket, setSocket }), [Socket, setSocket]);

  useEffect(() => {
    let data = localStorage.getItem("userData");
    if (data) {
      const parsedData = JSON.parse(data);
      // The localStorage stores data as { userData: {...} }
      const actualUserData = parsedData.userData || parsedData;
      setUser(actualUserData);
      getI18nData();
    }
    setIsInitialized(true);
    
    // Apply Safari-specific fixes
    if (isSafari()) {
      applySafariCSSFixes();
      
      // Add Safari class to body for CSS targeting
      document.body.classList.add('safari-browser');
      
      // Add iOS Safari class if applicable
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        document.body.classList.add('ios-safari');
      }
    }
  }, []);

  // New useEffect to handle timezone detection when user is loaded
  useEffect(() => {
    if (userData && token) {
      detectAndSaveTimezone(token);
      
      // Preload chat data in background
      console.log('[Chat Performance] Preloading chat data in background');
      ApiService.GROUP_CHAT_V2(userData._id || userData.id)
        .then(response => {
          console.log('[Chat Performance] Chat data preloaded successfully');
          // Store in global cache that DirectChat can access
          window.__chatDataCache = {
            data: [...(response.result.oneToOneChatData || []), ...(response.result.playlistChat || [])],
            timestamp: Date.now(),
            userId: userData._id || userData.id
          };
        })
        .catch(error => {
          console.error('[Chat Performance] Failed to preload chat data', error);
        });
    }
  }, [userData, token]);

  const getI18nData = async () => {
    try {
      const lng = cookieStorage.getCookie("language");
      // console.log('getI18nData - Making init API call to:', `${SERVER_URL}/init?language=${lng ? lng : "en"}&class=web`);
      
      const response = await ApiService.LANGUAGE(
        `language=${lng ? lng : "en"}&class=web`
      );
      const { availableLanguages, languageObject, selectedLanguage, token } =
        response.result;
      if (token) cookieStorage.createCookie("Authorization", token, 1);

      dispatch(
        setLanguage({
          language: selectedLanguage,
          languageObject: languageObject,
          availableLanguages: availableLanguages,
        })
      );
      cookieStorage.createCookie("language", selectedLanguage);
    } catch (error) {
      // console.error('getI18nData - Error fetching language data:', error);
      // console.error('getI18nData - API URL was:', `${SERVER_URL}/init?language=${cookieStorage.getCookie("language") || "en"}&class=web`);
      
      // Initialize with default English language
      i18next.init({
        interpolation: { escapeValue: false },
        lng: "en",
        resources: {
          en: {
            common: common_en,
          },
        },
      });
      
      // Set default language in Redux store
      dispatch(
        setLanguage({
          language: "en",
          languageObject: common_en,
          availableLanguages: ["en"],
        })
      );
    }
  };

  useEffect(() => {
    if (userData && token) {
      console.log('[Chat Performance] App.js - Initializing socket connection', {
        timestamp: new Date().toISOString()
      });
      const newSocket = io.connect(SERVER_URL, {
        auth: { JWT: token },
        transports: ["websocket"],
        reconnection: true,
      });
      
      newSocket.on('connect', () => {
        console.log('[Chat Performance] Socket connected', {
          timestamp: new Date().toISOString()
        });
      });
      
      newSocket.on('connect_error', (error) => {
        console.log('[Chat Performance] Socket connection error', {
          error,
          timestamp: new Date().toISOString()
        });
      });
      
      setSocket(newSocket);
      
      return () => {
        if (newSocket) {
          // console.log('App.js - Disconnecting socket');
          newSocket.disconnect();
        }
      };
    }
  }, [userData, token]);

  useEffect(() => {
    cookieStorage.createCookie("language", language ? language : "en");
    const common =
      Object.keys(languageObject).length === 0 ? common_en : languageObject;

    i18next.init({
      interpolation: { escapeValue: false },
      lng: language ? language : "en",
      resources: {
        [language]: {
          common: common,
        },
      },
    });
  }, [languageObject, language]);

  
  if (!isInitialized) {
    return <div style={{ backgroundColor: 'black', color: 'white', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || "MISSING_CLIENT_ID"}>
      <I18nextProvider i18n={i18next}>
        <AudioProvider>
          <SocketContext.Provider value={socket}>
            <UserContext.Provider value={value}>
              <BrowserRouter>
                <RouterOutlet />
              </BrowserRouter>
            </UserContext.Provider>
          </SocketContext.Provider>
        </AudioProvider>
      </I18nextProvider>
    </GoogleOAuthProvider>
  );
}

export default App;